import querystring from "querystring";

import { definedMap } from "@glideapps/ts-necessities";
import type * as React from "react";

import { MAPBOX_KEY } from "../const";
import { logError } from "../debug-print";
import type { ImageGravity } from "./image-types";
import { assert, getEmoji, mapFilterUndefined } from "../Support";
import { asString, deconstructLatLong, isLoadedDefined, loadedDefinedMap } from "./component-helpers";
import { isObjectValue } from "./computation-types";
import { Data } from "./data";
import { registerUIDeserializable } from "./deserialization";
import { geocodeAddress } from "./geocode";
import { imageURLHandlers } from "./image-handlers";
import type { ImageUrlOptions } from "./image-url-options";
import type {
    ActionSource,
    AppEnvironment,
    AppOther,
    ForceValueProducer,
    MapLocation,
    PotentialValue,
    RenderEnvironment,
    ValueProducer,
} from "./types";
import { LOADING, MapLocationKind, asAction } from "./types";
import type { ReactRenderer } from "./uis";
import { EmptyUI } from "./uis";

export enum ImageSourceKind {
    URLs,
    GeoCoordinates,
}

export enum MapTheme {
    Light,
    Dark,
}

interface URLsImageSource {
    readonly kind: ImageSourceKind.URLs;
    readonly urls: ReadonlyArray<string>;
}

interface MapImageSource {
    readonly kind: ImageSourceKind.GeoCoordinates;
    readonly location: MapLocation;
}

export type ImageSource = URLsImageSource | MapImageSource;

const googleDrivePrefix = "https://drive.google.com/uc?id=";

export function massageCloudURL(src: string, replaceHTTP: boolean = false): string {
    src = src.trim();

    if (replaceHTTP && src.toLowerCase().startsWith("http://")) {
        src = "https://" + src.substr(7);
    }

    return googleSearchImageResultUrl(massageGoogleDriveURL(dropboxUrl(src)));
}

export function massageImageUrl(
    src: string | undefined,
    options: ImageUrlOptions,
    appID: string | undefined
): string | undefined {
    if (src === undefined || options.width === 0 || options.height === 0) {
        return undefined;
    }
    const emoji = getEmoji(src);
    if (emoji !== undefined) {
        return emoji;
    }

    // Leave data: URIs alone; we can work with them directly.
    if (src.startsWith("data:") || src.startsWith("glide:")) return src;

    src = massageCloudURL(src, false);

    if (src.indexOf("://") === -1) return undefined;

    for (const h of imageURLHandlers) {
        const r = h.handle(src, options, appID);
        if (r !== undefined) return r;
    }

    return src;
}

export function firstImageURL(
    src: ImageSource,
    options: ImageUrlOptions,
    appID: string | undefined
): string | undefined {
    if (src.kind === ImageSourceKind.URLs && src.urls.length > 0) {
        return massageImageUrl(src.urls[0], options, appID);
    }
    return undefined;
}

const DROPBOX_FILE_REGEX = /dropbox.com\/s\/([^/]+)/;

function dropboxUrl(url: string): string {
    const match = DROPBOX_FILE_REGEX.exec(url);
    if (match !== null) {
        return `https://www.dropbox.com/s/${match[1]}?raw=1`;
    }
    return url;
}

// This will match strings like these:
//     https://drive.google.com/file/d/16HxoCwKjDzO3xxo2IiiEkgY1PkzEDM0v/view
//     https://drive.google.com/a/kpf.co.za/file/d/16HxoCwKjDzO3xxo2IiiEkgY1PkzEDM0v/view?usp=sharing
const GDRIVE_FILE_REGEX = /drive\.google\.com(\/a\/[^/]+)?\/file\/d\/([^/]+)/;

// This will match strings like these:
//     https://drive.google.com/open?id=1ggFIp7pKfxWPgtO6e2m6cLdgYLE7KVga
//     http://drive.google.com/uc?export=view&id=1ggFIp7pKfxWPgtO6e2m6cLdgYLE7KVga
//     https://drive.google.com/open?id=1ggFIp7pKfxWPgtO6e2m6cLdgYLE7KVga
//     https://drive.google.com/uc?id=1ggFIp7pKfxWPgtO6e2m6cLdgYLE7KVga
const GDRIVE_OPEN_REGEX = /drive\.google\.com\/(open|uc)\?(.+&)?id=([^/]+)/;

function makeGoogleDriveURL(id: string): string {
    return `${googleDrivePrefix}${id}`;
}

export function massageGoogleDriveURL(url: string): string {
    let match = GDRIVE_FILE_REGEX.exec(url);
    if (match !== null) {
        return makeGoogleDriveURL(match[2]);
    }

    match = GDRIVE_OPEN_REGEX.exec(url);
    if (match !== null) {
        return makeGoogleDriveURL(match[3]);
    }

    return url;
}

const GSEARCH_IMAGE_RESULT = /www.google.(.+)\/imgres?(.+)/;

// Google Search image results have the image URL embedded as a query parameter
// that we need to extract before displaying the image, otherwise it doesn't work.
function googleSearchImageResultUrl(rawUrl: string): string {
    const match = GSEARCH_IMAGE_RESULT.exec(rawUrl);
    if (match === null) return rawUrl;

    const query = querystring.parse(match[2]);
    if (typeof query.imgurl !== "string") return rawUrl;

    return query.imgurl;
}

export async function mapUrl(
    appEnvironment: AppEnvironment,
    src: ImageSource,
    width: number,
    height: number,
    zoom: number,
    mapTheme: MapTheme
): Promise<string | undefined> {
    if (src.kind === ImageSourceKind.URLs || width === undefined || width <= 0 || height === undefined || height <= 0) {
        return undefined;
    }

    // FIXME: This is a little messy. Handle the case when selecting a map from address type in tiles
    // But the address url is a generated image. The fix. Short circuit and return the generated image url. A better
    // fix would be to do validation on the dropdown in the builder and only allow selecting Map from Address
    // if the source column is not a glide url
    if (src.location.kind === MapLocationKind.Address && src.location.address.startsWith("glide:")) {
        return src.location.address;
    }

    let lat: number;
    let lng: number;
    if (src.location.kind !== MapLocationKind.LatLong) {
        // Because this is a "static map" we're not counting it against the geocode quota.
        const r = await geocodeAddress(appEnvironment, src.location.address, undefined);
        if (r === undefined) return undefined;
        lat = r.lat;
        lng = r.lng;
    } else {
        lat = src.location.latitude;
        lng = src.location.longitude;
    }
    width = Math.round(width);
    height = Math.round(height);
    const base = "https://api.mapbox.com/styles/v1/";
    const theme =
        mapTheme === MapTheme.Light ? "jassmith87/cjyhqzqit14fs1cmjwogd33l2/" : "jassmith87/cjyjgyfao1jcy1cqv858a8ruc/";
    const method = "static/";
    const pinImageURL = "https://res.cloudinary.com/glide/image/upload/v1572388650/glidehq/glide-pin.png";
    const overlay = `url-${encodeURIComponent(pinImageURL)}(${lng},${lat})/`;
    const location = `${lng},${lat},`;
    const zoomBearingPitch = `${zoom},0,0/`;
    const size = `${width}x${height}@2x`;
    const query = `?access_token=${MAPBOX_KEY}`;

    return base + theme + method + overlay + location + zoomBearingPitch + size + query;
}

export function canMakeUrl(src: ImageSource | undefined) {
    if (src === undefined) return false;
    return src.kind !== ImageSourceKind.URLs || src.urls.length > 0;
}

export async function imageSourceUrl(
    appEnvironment: AppEnvironment,
    src: ImageSource,
    width: number,
    height: number,
    zoom: number,
    mapTheme: MapTheme,
    gravity?: ImageGravity
): Promise<string | undefined> {
    if (src.kind === ImageSourceKind.URLs) {
        return firstImageURL(
            src,
            { thumbnail: height <= 75 && width <= 75, width, height, gravity },
            appEnvironment.appID
        );
    }
    return mapUrl(appEnvironment, src, width, height, zoom, mapTheme);
}

export function isImageSourceEmpty(src: ImageSource): boolean {
    return src.kind === ImageSourceKind.URLs && src.urls.length === 0;
}

export function evalSummary(
    title: PotentialValue,
    subtitle: PotentialValue,
    imageOrImages: ValueProducer | undefined,
    force: ForceValueProducer
): {
    title: string | undefined;
    subtitle: string | undefined;
    imageSource: ImageSource | undefined;
} {
    return {
        title: loadedDefinedMap(title, asString),
        subtitle: loadedDefinedMap(subtitle, asString),
        imageSource: definedMap(imageOrImages, (vp: ValueProducer): ImageSource | undefined => {
            const latLong = deconstructLatLong(vp, force);
            if (isLoadedDefined(latLong)) {
                return {
                    kind: ImageSourceKind.GeoCoordinates,
                    location: { ...latLong, kind: MapLocationKind.LatLong },
                };
            }
            const inspector = vp.getObjectInspector(force);
            if (inspector === LOADING) return undefined;
            if (inspector !== undefined) {
                const addressVP = inspector.getProperty("address");
                if (addressVP !== undefined) {
                    const address = force(addressVP);
                    if (typeof address === "string") {
                        return {
                            kind: ImageSourceKind.GeoCoordinates,
                            location: { kind: MapLocationKind.Address, address },
                        };
                    }
                } else {
                    logError("Image source has incorrect format");
                }
                return undefined;
            }
            const i = force(vp);
            if (!isLoadedDefined(i)) return undefined;
            if (Array.isArray(i)) {
                return {
                    kind: ImageSourceKind.URLs,
                    urls: mapFilterUndefined(i, urlVP => loadedDefinedMap(force(urlVP), asString)),
                };
            } else {
                assert(!isObjectValue(i));
                return { kind: ImageSourceKind.URLs, urls: [asString(i)] };
            }
        }),
    };
}

export type DropImageHandler = (img: string) => void;

export function makeDropImageHandler(
    env: RenderEnvironment,
    onDropImage: PotentialValue,
    actionSource: ActionSource
): DropImageHandler | undefined {
    const { isBuilder } = env.appEnvironment;
    if (!isBuilder) return undefined;
    return loadedDefinedMap(
        onDropImage,
        o => (img: string) => asAction(o).run(Data.fromJSON<AppOther, AppEnvironment>(img), env, actionSource)
    );
}

export class EmptyRenderer implements ReactRenderer {
    public renderContent(): React.ReactNode {
        return null;
    }
}

export function registerPortableUIs(): void {
    registerUIDeserializable(EmptyUI, new EmptyRenderer());
}
